import { DECommonTable } from 'components/DataExpansionComponents/Common/CommonTable/CommonTable'
import { DataTableProvider } from 'components/DataTable'
import { ContactResponse } from 'models/contacts'
import useApiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { dataTableSortingStoreRepo } from '../../../../DataTable/DataTableSorting/DataTableSortingStore'
import { IColumnVisibility } from '../../../../DataTable/types'
import { useConvenienceDoorsTableCols } from './ConvenienceDoorsTableColumns'
import { FilterIdentifier } from 'models/saved_view'
import { ConvenienceDoorsFilterset } from './ConvenienceDoorsFilterset'
import { StandardSummaryResponse } from 'models/paginated-response'
import ConvenienceDoorExportModal from 'components/Modals/ExportModal/ConvenienceDoorExportModal'
import { useUnifiedDoorsTableActions } from 'services/opportunities/useUnifiedDoorsTableActions'

type SortableFields = (keyof ContactResponse | string)[]

// Convenience door columns nest under `cstore` (GooglePlaceBaseDoorTableColumns
// accesor="cstore", detail link `/convenience/door/<cstore.id>`). App vertical
// `convenience` maps to unified `cstore`.
const UNIFIED_ROW_CONFIG = { accessor: 'cstore' }

const sortableFields: SortableFields = [
  'note_count',
  'contact_count',
  'deal_count',
  'name',
  'address',
  'city',
  'state',
  'country',
  'phone',
  'website',
  'reviews_avg',
  'reviews_cnt',
  'chain_doors_cnt',
]

export interface ConvenienceTableProps {
  setTotalRowsCount?: (count: StandardSummaryResponse) => void
  pageParamName?: string
  baseFilters?: Record<string, any>
  tableKey: string
  filterIdentifierModifier?: FilterIdentifier
  hideCreateCompanyButton?: boolean
}

function ConvenienceDoorTableComponent(props: ConvenienceTableProps) {
  const api = useApiService()
  const { tableKey, ...rest } = props
  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()
  const columns = useConvenienceDoorsTableCols({
    sorting,
    inCampaigns: props.baseFilters?.main_campaign,
  })
  const { listAction, summaryAction } = useUnifiedDoorsTableActions(
    'convenience',
    UNIFIED_ROW_CONFIG,
    {
      listAction: api.getConvenienceDoorList,
      summaryAction: api.getConvenienceDoorSummary,
    }
  )
  return (
    <DECommonTable
      {...rest}
      tableKey={tableKey}
      columns={columns}
      sortableFields={sortableFields}
      filterIdentifier="CONVENIENCE_DOOR_TABLE"
      apiGetListAction={listAction}
      apiGetSummaryAction={summaryAction}
      companyTypeSlug="convenience"
      tableTypeSlug="convenience-doors"
      searchPlaceholder="Search convenience stores by name"
      FilterSet={({ filterIdentifier, hideCampaignsFilter }) => (
        <ConvenienceDoorsFilterset
          filterIdentifier={filterIdentifier}
          hideCampaignsFilter={hideCampaignsFilter}
        />
      )}
      ExportModal={ConvenienceDoorExportModal}
      chainProxyIdAccessorKey="chain_proxy.id"
      doorProxyIdAccessorKey="id"
      isDoorsTable={true}
    />
  )
}

export function ConvenienceDoorTable(
  props: ConvenienceTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      defaultColumnVisibility={{
        name: true,
        address: true,
        city: true,
        state: true,
        place_url: true,
        phone: true,
        door_taglist: true,
        door_distributors: true,
        reviews_avg: true,
        zipcode: true,
        country: false,
        reviews_cnt: false,
        website: false,
        chain_doors_cnt: false,
        deal_count: false,
        campaigns: false,
        sales_stages: false,
        account_owners: false,
        chain_proxy_taglist: false,
        note_count: false,
        chain_proxy_distributors: false,
        ...defaultColumnVisibility,
      }}
      initialColumnOrder={[
        'name',
        'address',
        'city',
        'state',
        'zipcode',
        'place_url',
        'phone',
        'door_taglist',
        'door_distributors',
        'reviews_avg',
      ]}
      savedPresetsIdentifier="CONVENIENCE_DOOR_TABLE"
      preferredGroupOrder={['Target Attributes', 'Campaigns and Deals']}
    >
      <DataTableProvider tableKey={tableKey}>
        <ConvenienceDoorTableComponent tableKey={tableKey} {...rest} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
